import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import * as Yup from "yup";
import AuthForm from "../AuthForm/AuthForm";
import { MyTextField } from "../MyTextField";
import { apiAuthorization } from "../../utils/apiAuth";
import { clearSession } from "../../utils/authSession";
import { useAppDispatch } from "../../redux/hooks";
import { authorization } from "../../redux/Reducers/userSlice";

export const PasswordChange: React.FC = () => {
  const dispatch = useAppDispatch();
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get("token") || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const valid = /^[a-f0-9]{64}$/.test(token);
  useEffect(() => {
    window.history.replaceState(window.history.state, "", window.location.pathname);
  }, []);
  const handleSubmit = async ({ newPassword }: { newPassword: string }) => {
    setLoading(true);
    setError("");
    try {
      const response = await apiAuthorization().passwordChange({ token, newPassword });
      clearSession();
      dispatch(authorization(false));
      setMessage(response.data.message);
    } catch (e: any) {
      setError(
        e.response?.status === 400
          ? "This link is invalid or expired. Request a new reset link."
          : e.response?.status === 429
          ? "Too many attempts. Try again in 15 minutes."
          : "We could not confirm the password change. Try signing in or request a new link."
      );
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Helmet>
        <title>Choose a New Password</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="referrer" content="no-referrer" />
      </Helmet>
      <AuthForm
        titleAuthorization="Choose a new password"
        initialValues={{ newPassword: "", confirmation: "" }}
        validationSchema={Yup.object({
          newPassword: Yup.string()
            .min(12, "Use at least 12 characters.")
            .test(
              "bytes",
              "Use no more than 72 UTF-8 bytes.",
              (value) => new TextEncoder().encode(value || "").length <= 72
            )
            .required("Enter a password."),
          confirmation: Yup.string()
            .oneOf([Yup.ref("newPassword")], "Passwords do not match.")
            .required("Confirm your password."),
        })}
        textButton="Save new password"
        handleSubmit={handleSubmit}
        error={error}
        loading={loading}
        showError={Boolean(error)}
        showLinkForgotPassword={false}
        message={!valid ? "This reset link is incomplete. Please request a new link." : message}
        completed={!valid || Boolean(message)}
        textQuestion={message ? "Password updated. " : "Need another link? "}
        textAnswer={message ? "Sign in" : "Request password reset"}
        pathOnAnotherAuthorization={message ? "/signin/" : "/forgot/"}
      >
        <MyTextField
          nameLabel="New password"
          type="password"
          name="newPassword"
          component="input"
          id="newPassword"
          autoComplete="new-password"
        />
        <MyTextField
          nameLabel="Confirm new password"
          type="password"
          name="confirmation"
          component="input"
          id="confirmation"
          autoComplete="new-password"
        />
      </AuthForm>
    </>
  );
};
